import React from 'react';
import { Link } from 'react-router-dom';
import { formatOrganizationEndDate } from '../utils/organizationEndDate';
import { useOrganizationActions } from '../hooks/useOrganizationActions';

const TableView = ({ organizations, onChanged }) => {
  const { busy, setStatus, createSupportUser } = useOrganizationActions({ onChanged });

  const toggleStatus = (company) =>
    setStatus(company, company.subscriptionStatus === "Paused" ? "Active" : "Paused");

  // Interim soft-delete pending the Epic C2 delete-tenant modal.
  const handleDelete = (company) => {
    if (window.confirm(`Set ${company.name} to Deleted?`)) {
      setStatus(company, "Deleted");
    }
  };

  const getStatusBadge = (status) => {
    if (status === "Active") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          {status}
        </span>
      );
    } else if (status === "Deleted") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
          {status}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          {status}
        </span>
      );
    }
  };

  const getSubscriptionBadge = (subscriptionType) => {
    const type = subscriptionType || 'Free';
    if (type === 'Enterprise') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {type}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
          {type}
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
      {/* Table Header */}
      <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
        <div className="grid grid-cols-7 gap-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <div className="flex items-center">
            <i className="fas fa-building mr-2"></i>
            Name
          </div>
          <div className="flex items-center">
            <i className="fas fa-users mr-2"></i>
            Users
          </div>
          <div className="flex items-center">
            <i className="fas fa-tags mr-2"></i>
            Labels
          </div>
          <div className="flex items-center">
            <i className="fas fa-print mr-2"></i>
            Printer Models
          </div>
          <div className="flex items-center">
            <i className="fas fa-chart-bar mr-2"></i>
            Status
          </div>
          <div className="flex items-center">
            <i className="fas fa-calendar-times mr-2"></i>
            End-date
          </div>
          <div className="flex items-center">
            <i className="fas fa-calendar mr-2"></i>
            Created
          </div>
        </div>
      </div>

      {/* Table Body */}
      <div className="divide-y divide-gray-200 dark:divide-gray-600">
        {organizations.map((organization, index) => {
          const endDate = formatOrganizationEndDate(organization);
          return (
          <div key={organization.id} className="px-6 py-4 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 dark:hover:from-gray-700 dark:hover:to-gray-600 transition-all duration-300">
            <div className="grid grid-cols-7 gap-4 items-center">
              {/* Name */}
              <div className="flex items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-500 rounded-xl flex items-center justify-center mr-3 border border-gray-200 dark:border-gray-500 shadow-sm">
                  <span className="text-gray-600 dark:text-gray-300 text-sm font-medium">
                    {organization.name?.charAt(0) || '?'}
                  </span>
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {organization.name}
                </div>
              </div>

              {/* Users */}
              <div className="text-sm text-gray-900 dark:text-white">
                {organization.maxUsers || 0}
              </div>

              {/* Labels */}
              <div className="text-sm text-gray-900 dark:text-white">
                {organization.maxLabels || 0}
              </div>

              {/* Printer Models */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900 dark:text-white">
                  {organization.maxPrinterModels || 0}
                </span>
                {getSubscriptionBadge(organization.subscriptionType)}
              </div>

              {/* Status */}
              <div>
                {getStatusBadge(organization.subscriptionStatus)}
              </div>

              {/* End-date */}
              <div
                data-testid={`table-end-date-${organization.id}`}
                className={`text-sm ${
                  endDate && endDate.isPast
                    ? 'text-red-600 dark:text-red-400 font-medium'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                {endDate ? endDate.label : '—'}
              </div>

              {/* Created */}
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(organization.createdAt)}
              </div>
            </div>

            {/* Action Buttons Row */}
            <div className="mt-3 flex items-center space-x-3">
              <Link to={`organization/${organization.id}`}>
                <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center">
                  <i className="fas fa-edit mr-1"></i>
                  Edit
                </button>
              </Link>
              
              {organization.subscriptionStatus === "Paused" ? (
                <button
                  onClick={() => toggleStatus(organization)}
                  disabled={busy}
                  className="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 flex items-center disabled:opacity-50"
                >
                  <i className="fas fa-play mr-1"></i>
                  Activate
                </button>
              ) : (
                <button
                  onClick={() => toggleStatus(organization)}
                  disabled={busy}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 flex items-center disabled:opacity-50"
                >
                  <i className="fas fa-pause mr-1"></i>
                  Pause
                </button>
              )}

              <button
                onClick={() => createSupportUser(organization)}
                disabled={busy}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center disabled:opacity-50"
              >
                <i className="fas fa-life-ring mr-1"></i>
                Support
              </button>

              <button
                onClick={() => handleDelete(organization)}
                disabled={busy}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 flex items-center disabled:opacity-50"
              >
                <i className="fas fa-trash mr-1"></i>
                Delete
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default TableView;
